import { getMixedPalette } from "design-system/lib/chart-utils"
import { ChartConfig } from "design-system/molecules/Chart"
import {
  aggregateBucket,
  BiAggregation,
  BiBucket,
  getBiAggregation,
  getBiBuckets,
} from "modules/backoffice/lib/bi/biBuckets"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  BiCategory,
  BiCategoryField,
  BiMetricsResponseDataSchema,
  DateRange,
  TimeDimension,
} from "schema"
import { z } from "zod"

type CategoryMetrics = {
  fieldsWithoutValues: {
    [field: string]: {
      [date: string]: number
    }
  }
  fieldsWithValues: {
    [field: string]: {
      [value: string]: {
        [date: string]: number
      }
    }
  }
}

type GroupedMetrics = {
  [category: string]: CategoryMetrics
}

type GroupedMetricsByType = {
  Money: GroupedMetrics
  Count: GroupedMetrics
  Rate: GroupedMetrics
}

type ChartDataPoint = {
  date: string
  [key: string]: string | number
}

export type MetricGroup = {
  category: string
  field: string
  chartData: ChartDataPoint[]
  chartConfig: ChartConfig
  hasData: boolean
  type: "Money" | "Count" | "Rate"
  title: string
  description: string
}

type CategoryCopyBase = {
  title: string
  description: string
}

type CategoriesCopies = {
  [K in keyof typeof BiCategory]: CategoryCopyBase &
    ((typeof BiCategory)[K] extends keyof typeof BiCategoryField
      ? Partial<{
          [F in keyof (typeof BiCategoryField)[(typeof BiCategory)[K]]]: CategoryCopyBase
        }>
      : Record<string, never>)
}

type UseBiMetricsParams = {
  data: z.infer<typeof BiMetricsResponseDataSchema> | undefined
  range: DateRange
  timeDimension: TimeDimension
}

const chartColors = getMixedPalette()

const STANDALONE_FIELDS = new Set<string>([
  `${BiCategory.Organization}.${BiCategoryField.Organization.SubscriptionPrice}`,
  `${BiCategory.Organization}.${BiCategoryField.Organization.SubscriptionCurrentPrice}`,
  `${BiCategory.Message}.${BiCategoryField.Message.Billable}`,
])

export const useBiMetrics = ({ data, range, timeDimension }: UseBiMetricsParams) => {
  const { t } = useTranslation()

  const buckets = useMemo(() => getBiBuckets(range, timeDimension), [range, timeDimension])

  const groupedMetrics = useMemo(() => {
    if (!data) return { Money: {}, Count: {}, Rate: {} }

    const grouped: GroupedMetricsByType = {
      Money: {},
      Count: {},
      Rate: {},
    }

    for (const metric of data) {
      const type = metric.type
      const category = metric.category
      const field = metric.field
      const value = metric.value
      const date = metric.date
      const metricValue = type === "Count" ? (metric.count ?? 0) : (metric.amount ?? 0)

      if (!grouped[type][category]) {
        grouped[type][category] = {
          fieldsWithoutValues: {},
          fieldsWithValues: {},
        }
      }

      if (value) {
        if (!grouped[type][category].fieldsWithValues[field]) {
          grouped[type][category].fieldsWithValues[field] = {}
        }
        if (!grouped[type][category].fieldsWithValues[field][value]) {
          grouped[type][category].fieldsWithValues[field][value] = {}
        }
        grouped[type][category].fieldsWithValues[field][value][date] = metricValue
      } else {
        if (!grouped[type][category].fieldsWithoutValues[field]) {
          grouped[type][category].fieldsWithoutValues[field] = {}
        }
        grouped[type][category].fieldsWithoutValues[field][date] = metricValue
      }
    }

    return grouped
  }, [data])

  const categoriesCopies = useMemo(() => {
    return t("backoffice.bi.categories", { returnObjects: true }) as CategoriesCopies
  }, [t])

  const metricGroups = useMemo<{
    Money: MetricGroup[]
    Count: MetricGroup[]
    Rate: MetricGroup[]
  }>(() => {
    const moneyGroups: MetricGroup[] = []
    const countGroups: MetricGroup[] = []
    const rateGroups: MetricGroup[] = []

    const categoryOrder = Object.values(BiCategory)

    const getCategoryCopy = (category: string, field?: string): CategoryCopyBase => {
      const categoryCopy = categoriesCopies[category as keyof typeof categoriesCopies]

      if (!categoryCopy) {
        return { title: category, description: `Metrics for ${category} category` }
      }

      if (
        field &&
        field in categoryCopy &&
        typeof categoryCopy[field as keyof typeof categoryCopy] === "object"
      ) {
        return categoryCopy[field as keyof typeof categoryCopy] as unknown as CategoryCopyBase
      }

      return categoryCopy as CategoryCopyBase
    }

    const processCategory = (
      category: string,
      categoryData: CategoryMetrics,
      type: "Money" | "Count" | "Rate",
    ) => {
      let groups: MetricGroup[] = []
      if (type === "Money") groups = moneyGroups
      if (type === "Count") groups = countGroups
      if (type === "Rate") groups = rateGroups

      const createSingleFieldChart = (field: string, dateData: Record<string, number>) => {
        const aggregation = getBiAggregation({ type, category, field })
        const chartData = buckets.map((bucket) => {
          const entry: ChartDataPoint = {
            date: bucket.label,
            [field]: toBucketValue(dateData, bucket, aggregation),
          }
          return entry
        })

        const hasData = chartData.some((d) => {
          const val = d[field]
          return typeof val === "number" && val > 0
        })

        const chartConfig: ChartConfig = {
          [field]: {
            label: field,
            color: `var(--${chartColors[0]})`,
          },
        }

        const categoryCopy = getCategoryCopy(category, field)
        groups.push({
          category,
          field,
          chartData,
          chartConfig,
          hasData,
          type,
          title: categoryCopy.title,
          description: categoryCopy.description,
        })
      }

      const fieldsWithoutValues = categoryData.fieldsWithoutValues
      if (Object.keys(fieldsWithoutValues).length > 0) {
        const standaloneFields: Record<string, Record<string, number>> = {}
        const otherFields: Record<string, Record<string, number>> = {}

        Object.entries(fieldsWithoutValues).forEach(([field, dateData]) => {
          if (STANDALONE_FIELDS.has(`${category}.${field}`)) {
            standaloneFields[field] = dateData
          } else {
            otherFields[field] = dateData
          }
        })

        Object.entries(standaloneFields).forEach(([field, dateData]) => {
          createSingleFieldChart(field, dateData)
        })

        // Handle remaining fields
        if (Object.keys(otherFields).length > 0) {
          if (type === "Rate") {
            // Rate type: separate chart for each field
            Object.entries(otherFields).forEach(([field, dateData]) => {
              createSingleFieldChart(field, dateData)
            })
          } else {
            // Other types: group fields together
            const chartData = buckets.map((bucket) => {
              const entry: ChartDataPoint = {
                date: bucket.label,
              }

              Object.entries(otherFields).forEach(([field, dateData]) => {
                entry[field] = toBucketValue(
                  dateData,
                  bucket,
                  getBiAggregation({ type, category, field }),
                )
              })

              return entry
            })

            const hasData = chartData.some((d) =>
              Object.keys(d).some((key) => {
                if (key === "date") return false
                const val = d[key]
                return typeof val === "number" && val > 0
              }),
            )

            const chartConfig: ChartConfig = {}
            const fieldKeys = Object.keys(otherFields)

            fieldKeys.forEach((field, index) => {
              chartConfig[field] = {
                label: field,
                color: `var(--${chartColors[index % chartColors.length]})`,
              }
            })

            const categoryCopy = getCategoryCopy(category)
            groups.push({
              category,
              field: fieldKeys.join(","),
              chartData,
              chartConfig,
              hasData,
              type,
              title: categoryCopy.title,
              description: categoryCopy.description,
            })
          }
        }
      }

      // Sort fieldsWithValues by total values (high to low)
      const sortedFieldsWithValues = Object.entries(categoryData.fieldsWithValues).sort(
        ([, valuesA], [, valuesB]) => {
          const totalA = Object.values(valuesA).reduce((sum, dateData) => {
            return sum + Object.values(dateData).reduce((dateSum, value) => dateSum + value, 0)
          }, 0)
          const totalB = Object.values(valuesB).reduce((sum, dateData) => {
            return sum + Object.values(dateData).reduce((dateSum, value) => dateSum + value, 0)
          }, 0)
          return totalB - totalA
        },
      )

      sortedFieldsWithValues.forEach(([field, values]) => {
        const aggregation = getBiAggregation({ type, category, field })
        const chartData: ChartDataPoint[] = buckets.map((bucket) => {
          const entry: ChartDataPoint = {
            date: bucket.label,
          }

          // Sort values by total (high to low) for consistent ordering
          const sortedValues = Object.entries(values).sort(([, dateDataA], [, dateDataB]) => {
            const totalA = Object.values(dateDataA).reduce((sum, value) => sum + value, 0)
            const totalB = Object.values(dateDataB).reduce((sum, value) => sum + value, 0)
            return totalB - totalA
          })

          sortedValues.forEach(([value, dateData]) => {
            entry[value] = toBucketValue(dateData, bucket, aggregation)
          })

          return entry
        })

        const hasData = chartData.some((d) =>
          Object.keys(d).some((key) => {
            if (key === "date") return false
            const val = d[key]
            return typeof val === "number" && val > 0
          }),
        )

        const chartConfig: ChartConfig = {}
        const sortedValueKeys = Object.entries(values)
          .sort(([, dateDataA], [, dateDataB]) => {
            const totalA = Object.values(dateDataA).reduce((sum, value) => sum + value, 0)
            const totalB = Object.values(dateDataB).reduce((sum, value) => sum + value, 0)
            return totalB - totalA
          })
          .map(([value]) => value)

        sortedValueKeys.forEach((value, index) => {
          chartConfig[value] = {
            label: value,
            color: `var(--${chartColors[index % chartColors.length]})`,
          }
        })

        const categoryCopy = getCategoryCopy(category, field)
        groups.push({
          category,
          field,
          chartData,
          chartConfig,
          hasData,
          type,
          title: categoryCopy.title,
          description: categoryCopy.description,
        })
      })
    }

    categoryOrder.forEach((category) => {
      if (groupedMetrics.Money[category]) {
        processCategory(category, groupedMetrics.Money[category], "Money")
      }
      if (groupedMetrics.Count[category]) {
        processCategory(category, groupedMetrics.Count[category], "Count")
      }
      if (groupedMetrics.Rate[category]) {
        processCategory(category, groupedMetrics.Rate[category], "Rate")
      }
    })

    return { Money: moneyGroups, Count: countGroups, Rate: rateGroups }
  }, [buckets, groupedMetrics.Money, groupedMetrics.Count, groupedMetrics.Rate, categoriesCopies])

  return metricGroups
}

const toBucketValue = (
  dateData: Record<string, number>,
  bucket: BiBucket,
  aggregation: BiAggregation,
) => {
  const values = bucket.days.map((day) => dateData[day]).filter((value) => value !== undefined)

  return aggregateBucket(values, aggregation)
}
